import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Row, Col, Button } from "reactstrap";
import InputComponent from "../InputComponent";
import { ClientAddressService } from "../../services/ClientAddressService";
import { ClientAddressDTO } from "../../models/ClientAddress/ClientAdressDTO";
import { AssistanceDTO } from "../../models/Assistance/AssistanceDTO";
import { EquipmentService } from "../../services/EquipmentService";
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import ButtonComponent from "../ButtonComponent";
import { toast } from "react-toastify";
import { CountryService } from "../../services/CountryService";
import { useAvailableModulesContext } from "../../context/AvailableModulesContext";
import { ClientService } from "../../services/ClientService";

L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const MapResizer = () => {
    const map = useMap();
    useEffect(() => {
        const timer = setTimeout(() => {
            map.invalidateSize();
        }, 300);
        return () => clearTimeout(timer);
    }, [map]);
    return null;
};

type Props = {
    assistance: AssistanceDTO;
};

export function AssistanceLocationComponent({ assistance }: Props) {
    const intl = useIntl();
    const clientAddressService = new ClientAddressService();
    const equipmentService = new EquipmentService();
    const [clientAddress, setClientAddress] = useState<ClientAddressDTO | null>(null);
    const [clientAddressId, setClientAddressId] = useState<string | null>(null);
    const [position, setPosition] = useState<[number, number] | null>(null);
    const [isLoadingLocation, setIsLoadingLocation] = useState(false);
    const defaultPosition: [number, number] = [41.1579, -8.6291]; // Porto, Portugal Caso o mapa não consiga encontrar a localização
    const [countryName, setCountryName] = useState<string | undefined>();
    const { availableModules } = useAvailableModulesContext();
    const isActiveLocation = availableModules?.includes("IsActiveLocation");
    const clientService = new ClientService();

    useEffect(() => {
        fetchData();
    }, [assistance]);

    useEffect(() => {
        const fetchClientAddress = async () => {
            if (!clientAddressId) {
                setClientAddress(null);
                setPosition(null);
                return;
            }

            try {
                const res = await clientAddressService.getById(Number(clientAddressId));
                if (res && res.success && res.obj) {
                    setClientAddress(res.obj);
                } else {
                    setClientAddress(null);
                    setPosition(null);
                }
            } catch (error) {
                setClientAddress(null);
                setPosition(null);
            }
        };

        fetchClientAddress();
    }, [clientAddressId]);

    useEffect(() => {
        geocode();
    }, [clientAddress]);

    async function geocode() {
    let addressString: string | undefined;

    if (!clientAddress) {
        addressString = `${assistance.client?.address}, ${assistance.client?.locality}, ${assistance.client?.postalCode}`;
        if (!addressString) {
            setPosition(null);
            return;
        }
    } else {
        setIsLoadingLocation(true);
        addressString = `${clientAddress?.street1}, ${clientAddress?.city}, ${clientAddress?.postalCode}`;
    }

    try {
        const response = await fetch(
            `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(addressString)}`
        );
        const data = await response.json();

        if (data && data.length > 0) {
            const lat = parseFloat(data[0].lat);
            const lon = parseFloat(data[0].lon);
            setPosition([lat, lon]);
        } else if (clientAddress) {
            const cityCountryString = `${clientAddress?.city}, ${clientAddress?.countryId?.toString()}`;
            const cityResponse = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(cityCountryString)}`
            );
            const cityData = await cityResponse.json();

            if (cityData && cityData.length > 0) {
                const lat = parseFloat(cityData[0].lat);
                const lon = parseFloat(cityData[0].lon);
                setPosition([lat, lon]);
            } else {
                setPosition(null);
            }
        } else {
            setPosition(null);
        }
    } catch (error) {
        toast.error(intl.formatMessage({ id: "Screens.Assistances.Location.Error" }));
        setPosition(null);
    } finally {
        setIsLoadingLocation(false);
    }
}

useEffect(() => {
    if (clientAddress?.country) {
        getCountryNameByCode().then((name) => {
            setCountryName(name);
        });
    }
}, [clientAddress?.country]);

    useEffect(() => {
        getCountryNameByCode().then((name) => {
            setCountryName(name);
        });
    }, [clientAddress?.country]);

    const getCountryNameByCode = async () => {
        const code=clientAddress?.countryId?.toString()
        const countryService = new CountryService();
        const country = await countryService.GetCountryById(Number(code));
        return country.obj?.name;
    }
    const fetchData = async () => {
        const equipamentId = assistance.equipment?.erpId ?? '';
        if (!equipamentId) {
            setClientAddressId(null);
            return;
        }
        const equip = await equipmentService.GetByIdErp(equipamentId.toString());
        if (equip && equip.success && equip.obj?.clientAddressId) {
            setClientAddressId(equip.obj.clientAddressId);
        } else {
            setClientAddressId(null);
        }
    };

    const handleOpenInMaps = () => {
        if (clientAddress) {
            const address = encodeURIComponent(`${clientAddress.street1}, ${clientAddress.city}, ${clientAddress.postalCode}, Portugal`);
            const mapsUrl = `https://www.google.com/maps/place/${address}`;
            window.open(mapsUrl, '_blank');
        }

    };

    return (
        <>
            <div style={{ marginTop: '15px' }}>
                <Row className="mb-3 align-items-center">
                    <Col>
                        <h5>{intl.formatMessage({ id: "Screens.EquipmentEdit.EquipmentLocation" })}</h5>
                    </Col>
                </Row>
            </div>
            <Row className="mb-3 align-items-center">
                <Col className="d-flex justify-content-end">
                    {clientAddress && (
                        <ButtonComponent
                            text={intl.formatMessage({ id: "Assistances.Location.OpenInMaps" })}
                            onClick={handleOpenInMaps}
                            className="text-white"
                            style={{ marginRight: '10px' }}
                        />
                    )}
                </Col>
            </Row>
            {isActiveLocation && (
                <div style={{
                    width: '100%',
                    height: '400px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    position: 'relative',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    marginBottom: '15px'
                }}>
                    {isLoadingLocation && (
                        <div style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 1000,
                            background: 'rgba(255, 255, 255, 0.9)',
                            padding: '8px 12px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                        }}>
                            A carregar localização...
                        </div>
                    )}
                    <MapContainer
                        key={position ? `${position[0]}-${position[1]}` : 'default'}
                        center={position || defaultPosition}
                        zoom={position ? 16 : 13}
                        style={{ height: '100%', width: '100%' }}
                        scrollWheelZoom={true}
                    >
                        <TileLayer
                            attribution='© <a href="https://osm.org/copyright">OpenStreetMap</a> contributors'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <MapResizer />
                        {position && (
                            <Marker position={position}>
                                <Popup>
                                    {clientAddress?.street1}, {clientAddress?.city}
                                    {clientAddress?.postalCode && <><br />{clientAddress.postalCode}</>}
                                </Popup>
                            </Marker>
                        )}
                    </MapContainer>
                </div>
            )}

            <Row style={{ margin: "0px", padding: "0px" }}>
                <InputComponent
                    xl={6} lg={6} md={6} sm={6} xs={12}
                    style={{ width: "100%" }}
                    type={"text"}
                    value={clientAddress?.street1 || ""}
                    label={intl.formatMessage({ id: "Screens.Client.Info.Street" })}
                    readOnly
                />
                <InputComponent
                    xl={6} lg={6} md={6} sm={6} xs={12}
                    style={{ width: "100%" }}
                    type={"text"}
                    value={clientAddress?.street2 || ""}
                    label={intl.formatMessage({ id: "Screens.Client.Info.Street2" })}
                    readOnly
                />
            </Row>

            <Row style={{ margin: "0px", padding: "0px" }}>
                <InputComponent
                    xl={6} lg={6} md={6} sm={6} xs={12}
                    style={{ width: "100%" }}
                    type={"text"}
                    value={clientAddress?.city || ""}
                    label={intl.formatMessage({ id: "Screens.Client.Info.City" })}
                    readOnly
                />
                <InputComponent
                    xl={6} lg={6} md={6} sm={6} xs={12}
                    style={{ width: "100%" }}
                    type={"text"}
                    value={countryName || ""}
                    label={intl.formatMessage({ id: "Screens.Client.Info.NameCountry" })}
                    readOnly
                />
            </Row>

            <Row style={{ margin: "0px", padding: "0px" }}>
                <InputComponent
                    xl={6} lg={6} md={6} sm={6} xs={12}
                    style={{ width: "100%" }}
                    type={"text"}
                    value={clientAddress?.postalCode || ""}
                    label={intl.formatMessage({ id: "Screens.Client.Info.PostalCode" })}
                    readOnly
                />
            </Row>
        </>
    );
}